import { createContext, useContext, useState, useCallback, useEffect } from "react";

const StocktakingContext = createContext(null);

export function StocktakingProvider({ children }) {
  const [isStocktakingActive, setIsStocktakingActive] = useState(false);
  const [stocktakingLocation, setStocktakingLocation] = useState(null);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Check localStorage on mount for persistence across page refreshes
  useEffect(() => {
    const savedLocation = localStorage.getItem("stocktaking_location");
    if (savedLocation) {
      setIsStocktakingActive(true);
      setStocktakingLocation(savedLocation);
    }
  }, []);

  const startStocktaking = useCallback((location) => {
    setIsStocktakingActive(true);
    setStocktakingLocation(location);
    localStorage.setItem("stocktaking_location", location);
  }, []);

  const finishStocktaking = useCallback(() => {
    setIsStocktakingActive(false);
    setStocktakingLocation(null);
    localStorage.removeItem("stocktaking_location");
    setShowExitWarning(false);
    setPendingNavigation(null);
  }, []);

  const requestNavigation = useCallback((path) => {
    if (isStocktakingActive && path !== "/scan") {
      setPendingNavigation(path);
      setShowExitWarning(true);
      return false; // Navigation blocked
    }
    return true; // Navigation allowed
  }, [isStocktakingActive]);

  const confirmExit = useCallback(() => {
    const path = pendingNavigation;
    finishStocktaking();
    return path;
  }, [pendingNavigation, finishStocktaking]);

  const cancelExit = useCallback(() => {
    setShowExitWarning(false);
    setPendingNavigation(null);
  }, []);

  return (
    <StocktakingContext.Provider value={{
      isStocktakingActive,
      stocktakingLocation,
      showExitWarning,
      pendingNavigation,
      startStocktaking,
      finishStocktaking,
      requestNavigation,
      confirmExit,
      cancelExit,
    }}>
      {children}
    </StocktakingContext.Provider>
  );
}

export function useStocktaking() {
  const context = useContext(StocktakingContext);
  if (!context) {
    throw new Error("useStocktaking must be used within StocktakingProvider");
  }
  return context;
}
